import {
  useState,
  useEffect,
  useRef,
  useCallback
} from "react";
import moment from "moment-timezone";
import axios from "axios";
import SearchBar from "./SearchBar.tsx";
import { useUserContext } from "../../context/UserContext.tsx";
import OuterFilters from "./OuterFilters.tsx";
import { Dialog } from "primereact/dialog";
import PlayerWithTimeline from "./PlayerWithTimeline.tsx";
import { toast } from "../../components/Toast/toast.ts";
import { DataTable as CustomDataTable } from "../../components/DataTable/index.ts";
import DescriptionBox from "../../components/DescriptionBox/index.tsx";
import LightTooltip from "../../components/CustomTooltip.tsx";

export default function SuspiciousAlert() {
  const [loading, setLoading] = useState(true); // Start with true
  const [txData, setTxData] = useState([]); // Keep as array
  //eslint-disable-next-line
  const [innerLoading, setInnerLoading] = useState(false);
  const [isAllDataFetched, setIsAllDataFetched] = useState(false);
  const [filterVal, setFilterVal] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const {
    globalstorevalue,
    activeRemoteUrl,
    storeInfoMap,
    timeZone,
    activeSiteId,
    posCamera,
  } = useUserContext();
  const [page, setPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [isSuspicious, setIsSuspicious] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [posId, setPosId] = useState<string>("");
  const [selectedPOSName, setSelectedPOSName] = useState("All POS");
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [liveSrc, setLiveSrc] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [recordingSrc, setRecordingSrc] = useState("");
  const [initialSeekTime, setInitialSeekTime] = useState<number | null>(null);
  const [cursorSeekTime, setCursorSeekTime] = useState<number | null>(null);
  const [currentTimestamp, setCurrentTimestamp] = useState<number | null>(null);
  const [isFisheye, setIsFisheye] = useState(false);
  const seekPosRef = useRef<number>(Date.now());
  const [timeline, setTimeline] = useState<Array<any>>([]);
  const [TxVideoHeader, setTxVideoHeader] = useState("");

  const remoteUrl = activeRemoteUrl;
  const siteId = activeSiteId;

  // const remoteUrl = "http://arthurst7-11.myliveeye.com:8000"

  // Add handleGetTimeline function
  const handleGetTimeline = async (cameraId) => {
    if (!activeRemoteUrl || !siteId || !cameraId) return [];

    try {
      const response = await axios.get(
        `${activeRemoteUrl}/v1/recordings/timeline/${cameraId}`,
      );

      if (response.status === 200) {
        const timelineData = response.data.success.timeline;
        // console.log("Timeline fetched:", timelineData.length, "items");
        setTimeline(timelineData);
        return timelineData;
      }
    } catch (error) {
      console.error(" Failed to fetch timeline:", error);
      toast.error("Failed to fetch timeline for this camera");
      return [];
    }
    return [];
  };

  const initialFilters = {
    posId: "",
    isSuspicious: "",
    paymentMethod: "",
    startDate: "",
    endDate: "",
    searchVal: "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      let updatedFilters;

      // Handle date range as a special case
      if (key === "dateRange") {
        updatedFilters = {
          ...prev,
          startDate: value.startDate,
          endDate: value.endDate,
        };
      } else {
        updatedFilters = { ...prev, [key]: value };
      }

      // Set filterVal to true if any filter has a value
      const hasActiveFilters = Object.values(updatedFilters).some(
        (val) => val !== "" && val !== null && val !== false,
      );
      setFilterVal(hasActiveFilters);

      // Call API immediately for all changes
      setTimeout(() => fetchFilteredData(updatedFilters), 0);

      return updatedFilters;
    });
  };

  const sendVideoDataToDataLayer = (transaction) => {
    try {
      if (!transaction?.siteName) return;

      const storeName = `${transaction.siteName} - ${transaction.posName}`;

      // ✅ ALWAYS initialize first
      window.dataLayer = window.dataLayer || [];

      // ✅ Push AFTER value exists
      window.dataLayer.push({
        event: "video_opened",
        videoHeader: storeName,
        storeName: storeName,
      });
    } catch (err) {
      console.error("sendVideoDataToDataLayer failed", err);
    }
  };

  const handleWatchPlayback = (transaction) => {
    if (!activeRemoteUrl || !transaction?.cameraId) {
      console.error("Missing remote URL or camera ID:", {
        activeRemoteUrl,
        cameraId: transaction?.cameraId,
      });
      return;
    }

    sendVideoDataToDataLayer(transaction);

    // Open dialog first
    setDialogOpen(true);

    const cameraId = transaction.cameraId;
    setSelectedCameraId(cameraId);
    setTxVideoHeader(transaction.siteName);

    // LIVE URL (used only when switching back to live)
    const src = `${activeRemoteUrl}/${cameraId}_high/whep`;
    setLiveSrc(src);

    // Validate cameraId
    if (!cameraId) {
      console.warn("Missing cameraId in transaction:", transaction);
      alert("Camera ID not available");
      return;
    }

    // Extract seek timestamp
    let seekPos;
    if (transaction.startedAt)
      seekPos = new Date(transaction.startedAt).getTime();
    else if (transaction.date) seekPos = new Date(transaction.date).getTime();
    else if (transaction.createdAt)
      seekPos = new Date(transaction.createdAt).getTime();
    else {
      alert("No valid date in transaction");
      return;
    }

    if (isNaN(seekPos)) {
      alert("Invalid transaction timestamp");
      return;
    }

    const preciseSeekPos = Math.floor(seekPos);
    //console.log("Precise seek position:", preciseSeekPos);

    // Reset everything for new playback
    seekPosRef.current = preciseSeekPos;
    setInitialSeekTime(preciseSeekPos); // <-- PlayerWithTimeline waits for this
    setCursorSeekTime(preciseSeekPos);
    setCurrentTimestamp(preciseSeekPos);

    setSelectedTransaction(transaction);
  };

  // Add this helper function at the top of your component or in a separate utils file
  // Build transaction API URL
  const buildTransactionURL = (page = 1, filters = {}) => {
    let url = `${remoteUrl}/v1/pos/transactions/${siteId}?page=${page}&sortBy=startedAt&order=DESC`;

    const {
      searchVal = "",
      paymentMethod = "",
      isSuspicious = "",
      posId = "",
      startDate = "",
      endDate = "",
    } = filters;

    if (searchVal.trim()) {
      url += `&searchValue=${encodeURIComponent(searchVal.trim())}`;
    }

    if (paymentMethod) {
      url += `&paymentMethod=${encodeURIComponent(paymentMethod)}`;
    }

    url += `&isSuspicious=${encodeURIComponent(
      isSuspicious === true ? "true" : "All Transactions",
    )}`;

    if (posId.trim()) {
      url += `&posId=${encodeURIComponent(posId.trim())}`;
    }

    if (startDate && endDate) {
      // Use moment to handle local time boundaries and formatting securely
      // This prevents the "off by one day" error caused by UTC conversion
      const formattedStart = moment(startDate)
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss.SSS");
      const formattedEnd = moment(endDate)
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm:ss.SSS");

      url += `&startDate=${formattedStart}&endDate=${formattedEnd}`;
    }

    return url;
  };

  // Filtered transaction fetch
  const fetchFilteredData = async (filters) => {
    if (!remoteUrl) return;

    try {
      setLoading(true);
      setTxData([]);
      setIsAllDataFetched(false);
      setPage(1);

      const url = buildTransactionURL(1, filters);
      const response = await axios.get(url);

      if (response.data?.success) {
        const transactions = response.data.success.transactions || [];
        setTxData(transactions);
        setPageCount(response.data.success.pagesCount);
        setIsAllDataFetched(transactions.length === 0);
      } else {
        setTxData([]);
        setIsAllDataFetched(true);
      }
    } catch (error) {
      console.log("Error in fetching Transaction data", error);
      setTxData([]);
      setPageCount(1);
      setIsAllDataFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // Update filterMoreData function
  // Load more filtered
  const filterMoreData = async (filters) => {
    if (!remoteUrl || isAllDataFetched || innerLoading) return;

    try {
      setInnerLoading(true);
      const nextPage = page + 1;
      const url = buildTransactionURL(nextPage, filters);
      const response = await axios.get(url);

      if (response.data?.success?.transactions) {
        const newTransactions = response.data.success.transactions;
        if (newTransactions.length > 0) {
          setTxData((prev) => [...prev, ...newTransactions]);
          setPage(nextPage);
          setIsAllDataFetched(nextPage >= pageCount);
        } else {
          setIsAllDataFetched(true);
        }
      }
    } catch (err) {
      console.error("Error in fetching More Transactions:", err);
    } finally {
      setInnerLoading(false);
    }
  };

  useEffect(() => {
    // Don't run search effect if searchVal is empty (from reset or initial state)
    if (!searchVal || searchVal.trim() === "") {
      return; // Exit early, don't make API call
    }

    const handler = setTimeout(() => {
      // Preserve ALL current filters and only update searchVal
      setFilters((prevFilters) => {
        const updatedFilters = {
          ...prevFilters, // Keep all existing filters
          searchVal: searchVal, // Only update search value
        };

        // Set filterVal to true if any filter has a value (including search)
        const hasActiveFilters = Object.values(updatedFilters).some(
          (val) => val !== "" && val !== null && val !== false,
        );
        setFilterVal(hasActiveFilters);

        // Call fetchFilteredData with the complete filter set
        fetchFilteredData(updatedFilters);

        return updatedFilters;
      });
    }, 500);

    return () => clearTimeout(handler);
  }, [searchVal]); // Keep only searchVal as dependency

  useEffect(() => {
    if (!storeInfoMap || Object.keys(storeInfoMap).length === 0) return;
    if (!remoteUrl || !siteId) return;

    const timeout = setTimeout(() => {
      setSelectedPOSName("All POS");
      setPosId("");
      setFilters((prevFilters) => ({
        ...prevFilters,
        posId: "",
      }));

      const currentFilters = { ...filters, posId: "" };
      const hasActiveFilters = Object.values(currentFilters).some(
        (val) => val !== "" && val !== null && val !== false,
      );

      if (hasActiveFilters) {
        fetchFilteredData(currentFilters);
      } else {
        fetchInitialTransactions();
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [globalstorevalue, remoteUrl, siteId]);

  // ✅ Initial load
  const fetchInitialTransactions = async () => {
    if (!remoteUrl || !siteId) return;

    try {
      setLoading(true);

      const url = buildTransactionURL(1, { isSuspicious: false });
      const response = await axios.get(url);

      if (response.data?.success) {
        const transactions = response.data.success.transactions || [];
        setTxData(transactions);
        setPage(1);
        setPageCount(response.data.success.pagesCount);
        setIsAllDataFetched(transactions.length === 0);
      } else {
        setTxData([]);
        setIsAllDataFetched(true);
      }
    } catch (error) {
      console.log("Error in fetching Initial Transaction data", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load more initial
  const fetchMoreTransactions = async () => {
    if (!remoteUrl || !siteId || innerLoading || isAllDataFetched) return;

    try {
      setInnerLoading(true);

      const nextPage = page + 1;
      const url = buildTransactionURL(nextPage, { isSuspicious: false });
      const response = await axios.get(url);

      if (response.data?.success?.transactions) {
        const newTransactions = response.data.success.transactions;
        setTxData((prev) => [...prev, ...newTransactions]);
        setPage(nextPage);
        setIsAllDataFetched(nextPage >= pageCount);
      } else {
        setIsAllDataFetched(true);
      }
    } catch (err) {
      console.log("Error in fetching more Transaction data", err);
    } finally {
      setInnerLoading(false);
    }
  };

  // Reset all filters
  const ResetFilters = () => {
    // Reset all filter-related state
    const resetFilters = { ...initialFilters };
    setFilters(resetFilters);
    setIsSuspicious(false);
    setPaymentMethod("All Methods");
    setPosId("");
    setStartDate("");
    setEndDate("");
    setFilterVal(false); // Set to false since no filters are active
    setPage(1);
    setIsAllDataFetched(false);
    // Clear search value WITHOUT triggering the search effect
    setSearchVal("");
    // Only call fetchFilteredData once with reset filters
    fetchFilteredData(resetFilters);
  };

  const handleDialogHide = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const txColumns: any[] = [
    {
      key: "index",
      label: "S.No.",
      width: "4%",
      render: (_: any, __: any, index: number) => (
        <span className="text-[rgb(var(--text-muted))] text-[12px]">
          {index + 1}
        </span>
      ),
    },
    {
      key: "siteName",
      label: "Store Name",
      width: "10%",
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[13px] text-[rgb(var(--text-primary))]">
          {value}
        </span>
      ),
    },
    {
      key: "goods",
      label: "Purchased Item",
      width: "15%",
      render: (value: any[]) => (
        <div className="flex flex-col gap-0.5">
          {value?.map((good: any, idx: number) => (
            <span
              key={idx}
              className="text-[12px] text-[rgb(var(--text-secondary))] break-all"
            >
              {good.amount} × {good.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "goods_price",
      label: "Price",
      width: "8%",
      render: (_: any, row: any) => (
        <div className="flex flex-col gap-0.5">
          {row?.goods?.map((good: any, idx: number) => (
            <span
              key={idx}
              className="text-[12px] text-[rgb(var(--text-secondary))]"
            >
              ${good.cost}
            </span>
          ))}
        </div>
      ),
    },
    ...(filters.isSuspicious
      ? [
          {
            key: "tag",
            label: "Suspicious Content",
            width: "12%",
            sortable: true,
            render: (value: string) => (
              <span className="text-[12px] text-[rgb(var(--danger))] font-medium">
                {value}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "subtotal",
      label: "SubTotal",
      width: "7%",
      sortable: true,
      render: (value: number) => (
        <span className="text-[12px]">${value?.toFixed(2)}</span>
      ),
    },
    {
      key: "tax",
      label: "Tax",
      width: "6%",
      sortable: true,
      render: (value: number) => (
        <span className="text-[12px]">${value?.toFixed(2)}</span>
      ),
    },
    {
      key: "total",
      label: "Total",
      width: "7%",
      sortable: true,
      render: (value: number) => (
        <span className="text-[12px] font-semibold">${value?.toFixed(2)}</span>
      ),
    },
    {
      key: "date",
      label: "Date",
      width: "12%",
      sortable: true,
      render: (value: string) => {
        if (!value) return <span>-</span>;
        const d = moment(value);
        return (
          <span className="text-[12px] text-[rgb(var(--text-secondary))]">
            {d.isValid() ? d.format("MM/DD/YYYY HH:mm") : "-"}
          </span>
        );
      },
    },
    {
      key: "paymentMethod",
      label: "Payment Method",
      width: "8%",
      sortable: true,
      render: (value: string) => (
        <span className="text-[11px] font-medium uppercase">{value}</span>
      ),
    },
    {
      key: "posName",
      label: "POS",
      width: "7%",
      sortable: true,
      render: (value: string) => <div className="w-full truncate" > <LightTooltip title={value} placement="top" arrow> <span className="text-[12px]">{value}</span> </LightTooltip> </div>,
    },
    {
      key: "playback",
      label: "Playback",
      width: "8%",
      align: "center",
      render: (_: any, row: any) => (
        <span
          className="text-[12px] font-medium text-gold-2 cursor-pointer hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            handleWatchPlayback(row);
          }}
        >
          Watch
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <CustomDataTable
          columns={txColumns}
          data={txData}
          rowKey="_id"
          loading={loading}
          skeletonRows={6}
          sortable={true}
          emptyMessage="No transaction data available."
          maxHeight="calc(100vh - 340px)"
          onScrollEnd={() =>
            filterVal ? filterMoreData(filters) : fetchMoreTransactions()
          }
          hasMore={!isAllDataFetched}
          loader={
            <div className="flex justify-center py-4">
              <span className="text-[12px] text-[rgb(var(--text-muted))]">
                Loading more...
              </span>
            </div>
          }
          endMessage={<span>No More Data</span>}
          header={
            <div className="flex flex-wrap justify-between gap-4">
              <DescriptionBox
                title="Alerts & Rules"
                subTitle="Suspicious Transaction Alerts"
                description="Monitor and review POS transaction alerts with video playback."
              />

              <div className="flex flex-wrap items-center gap-4" >
                <OuterFilters
                  filters={filters}
                  setFilters={setFilters}
                  initialFilters={initialFilters}
                  fetchInitialTransactions={fetchInitialTransactions}
                  handleFilterChange={handleFilterChange}
                  fetchFilteredData={fetchFilteredData}
                  setSearchVal={setSearchVal}
                  filterVal={filterVal}
                  setFilterVal={setFilterVal}
                  posId={posId}
                  selectedPOSName={selectedPOSName}
                  setSelectedPOSName={setSelectedPOSName}
                  startDate={startDate}
                  setStartDate={setStartDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  isSuspicious={isSuspicious}
                  setIsSuspicious={setIsSuspicious}
                  ResetFilters={ResetFilters}
                />

                <SearchBar searchVal={searchVal} setSearchVal={setSearchVal} />
              </div>
            </div>
          }
        />

        {dialogOpen && (
          <Dialog
            header={TxVideoHeader}
            visible={dialogOpen}
            maximized
            style={{ width: "100vw" }}
            breakpoints={{ "960px": "75vw", "641px": "100vw" }}
            onHide={handleDialogHide}
          >
            <div className="dialog-container">
              <div
                className="video-modal-container"
                style={{ height: "100vh", width: '100%' }}
              >
                <PlayerWithTimeline
                  src={liveSrc}
                  cameraId={selectedCameraId}
                  isFisheye={isFisheye}
                  timeZone={timeZone}
                  remoteUrl={activeRemoteUrl}
                  posCamera={posCamera}
                  initialSeekTime={initialSeekTime}
                  timeline={timeline}
                  handleGetTimeline={handleGetTimeline}
                  recordingSrc={recordingSrc}
                  setRecordingSrc={setRecordingSrc}
                  currentTimestamp={currentTimestamp}
                  setCurrentTimestamp={setCurrentTimestamp}
                  setInitialSeekTime={setInitialSeekTime}
                />
              </div>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}
